import { useState } from 'react';
import { Link, Outlet, useLocation, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HugeiconsIcon } from '@hugeicons/react';
import { AddSquareIcon } from '@hugeicons/core-free-icons';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { OrgSidebar } from './org-sidebar';
import { OrgTopbar } from './org-topbar';
import { NewTemplateDialog } from './new-templates-dialog';
import { useOrgUsersUiStore } from '../org-users/store/use-org-users-ui-store';
import { OrgRole } from '../org-permissions/types/permissions.types';

interface OrgShellProps {
  orgName?: string;
  orgSlug: string;
  orgId?: string;
  orgRole: OrgRole;
}

export const OrgShell = ({ orgName, orgSlug, orgId, orgRole }: OrgShellProps) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  // Exact match — the New Template button should only appear on the
  // templates list page, not on deeper routes like /templates/:id/edit
  // or /templates/new (which has its own context).
  const isTemplatesListRoute = useMatch('/org/:orgId/templates');
  const isUsersListRoute = useMatch('/org/:orgId/users');
  const isContractCheckerListRoute = useMatch('/org/:orgId/contract-checker');
  const isDocxBuilderRoute = useMatch('/org/:orgId/templates/:id/docx');
  const canManageTemplates = orgRole === 'org_admin' || orgRole === 'org_editor';
  const canInviteMembers = orgRole === 'org_admin';
  const openInviteDialog = useOrgUsersUiStore((s) => s.openInviteDialog);

  // Full-screen for builder pages (no sidebar/topbar)
  const isBuilderPage = location.pathname.includes('/builder');
  if (isBuilderPage) {
    return <Outlet />;
  }

  // Topbar action — route-aware. OrgTopbar is itself `md:hidden`, so any
  // action rendered here only appears on mobile.
  let topbarAction: React.ReactNode | undefined;
  if (isTemplatesListRoute && canManageTemplates && orgId) {
    topbarAction = <NewTemplateDialog orgSlug={orgSlug} orgName={orgName ?? ''} orgId={orgId} />;
  } else if (isUsersListRoute && canInviteMembers) {
    // Just the trigger button — the actual dialog stays mounted in
    // OrgUsersPage. Zustand bridges the two: tap here, page opens.
    topbarAction = (
      <Button
        variant="primary"
        size="sm"
        onClick={openInviteDialog}
        aria-label={t('ORGTEAM_HEADER_INVITEMEMBER')}
      >
        <HugeiconsIcon icon={AddSquareIcon} size={20} />
        {t('ORGTEAM_HEADER_INVITEMEMBER')}
      </Button>
    );
  } else if (isContractCheckerListRoute) {
    topbarAction = (
      <Button asChild variant="primary" size="sm">
        <Link
          to={`/org/${orgSlug}/contract-checker/new`}
          className="inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('ORGCONTRACTCHECKER_LIST_NEW', { defaultValue: 'New Analysis' })}
        </Link>
      </Button>
    );
  }

  return (
    <div className="font-nunito">
      {/* Header is h-16 (64px) and sits above this shell in MainLayout,
          so the shell must claim the remaining viewport — not 100vh. */}
      <div className="flex h-[calc(100vh-4.05rem)] overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block">
          <OrgSidebar orgName={orgName ?? ''} orgSlug={orgSlug} orgRole={orgRole} />
        </aside>

        {/* Mobile Sidebar (Radix Dialog drawer) — always mounted so
            open/close animations and focus management work properly. */}
        <OrgSidebar
          orgName={orgName ?? ''}
          orgSlug={orgSlug}
          orgRole={orgRole}
          isMobile
          open={mobileSidebarOpen}
          onOpenChange={setMobileSidebarOpen}
        />

        {/* Main Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <OrgTopbar
            onMenuClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            action={topbarAction}
          />
          <main className="flex-1 overflow-y-auto">
            {isDocxBuilderRoute ? (
              <Outlet />
            ) : (
              <div className="w-full py-4 md:py-6 px-3 md:px-4 lg:px-8">
                <Outlet />
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
